import { Link } from 'react-router-dom';
import { coupons } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function ContentSections() {
  return (
    <>
      {/* What is Sugargoo Spreadsheet */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3 text-center">
            What Is Sugargoo Spreadsheet?
          </h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-3xl mx-auto text-center mb-10">
            A <strong>Sugargoo Spreadsheet</strong> is a structured product collection system designed to help shoppers discover high-quality items faster and more efficiently. Instead of spending hours searching through individual listings, users can browse curated spreadsheets organized by category, compare prices side by side, and access verified product links with QC references.
          </p>

          <div className="overflow-x-auto">
            <table className="w-full max-w-3xl mx-auto border-collapse bg-background-50 rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-background-200">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground-800">Feature</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground-800">Traditional Product Searching</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-primary-700">Sugargoo Spreadsheet</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200/70">
                {[
                  { feature: 'Finding Products', traditional: 'Slow & Manual', spreadsheet: 'Organized & Fast' },
                  { feature: 'Product Categories', traditional: 'Limited', spreadsheet: 'Extensive & Curated' },
                  { feature: 'Price Comparison', traditional: 'Difficult', spreadsheet: 'Easy Side-by-Side' },
                  { feature: 'Beginner Friendly', traditional: 'Low', spreadsheet: 'High' },
                  { feature: 'QC Reference', traditional: 'Rare', spreadsheet: 'Built-in' },
                ].map((row) => (
                  <tr key={row.feature} className="hover:bg-background-50/50">
                    <td className="px-4 py-3 text-sm font-medium text-foreground-800">{row.feature}</td>
                    <td className="px-4 py-3 text-sm text-foreground-600">{row.traditional}</td>
                    <td className="px-4 py-3 text-sm text-primary-700 font-medium">{row.spreadsheet}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Why Thousands Search Sugargoo Spreadsheet */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3 text-center">
            Why Thousands Of Users Search Sugargoo Spreadsheet
          </h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto text-center mb-10">
            The Sugargoo spreadsheet has become an essential tool for savvy international shoppers who want to save time, discover trending products, and make informed purchasing decisions.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {[
              { title: 'Save Research Time', desc: 'Skip hours of individual product searching. Access thousands of verified finds organized in one comprehensive spreadsheet.', icon: 'ri-time-line' },
              { title: 'Discover Trending Items', desc: 'Stay ahead with regularly updated product lists featuring the latest viral fashion, sneakers, and accessories from Sugargoo.', icon: 'ri-fire-line' },
              { title: 'Compare Products Easily', desc: 'Side-by-side product listings make price comparison and quality evaluation straightforward for every category.', icon: 'ri-scales-line' },
              { title: 'Learn Shopping Strategies', desc: 'Each spreadsheet includes tips on sizing, shipping optimization, and how to get the best value from your Sugargoo orders.', icon: 'ri-lightbulb-line' },
              { title: 'Avoid Common Mistakes', desc: 'Benefit from community-vetted products and QC notes that help you avoid low-quality sellers and common purchasing pitfalls.', icon: 'ri-shield-check-line' },
              { title: 'Beginner-Friendly Guides', desc: 'Whether you are new to Sugargoo or an experienced buyer, the spreadsheets include clear instructions for every skill level.', icon: 'ri-user-heart-line' },
            ].map((item) => (
              <div key={item.title} className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200/50 hover:border-primary-200 transition-colors">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 mb-4">
                  <i className={`${item.icon} text-primary-600 text-lg`}></i>
                </div>
                <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 mb-2">{item.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sugargoo Review 2026 */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3 text-center">
            Sugargoo Review 2026
          </h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto text-center mb-10">
            An honest, detailed look at the Sugargoo shopping agent platform — what works, what could improve, and whether it is the right choice for your international shopping needs.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-5 rounded-lg bg-background-50 border border-secondary-200/50">
              <h3 className="font-heading text-lg font-semibold text-secondary-700 mb-3 flex items-center gap-2">
                <i className="ri-thumb-up-line"></i> Advantages
              </h3>
              <ul className="space-y-2 text-sm text-foreground-700">
                {['User-friendly interface suitable for beginners', 'Multiple shipping methods with competitive rates', 'Free warehouse storage for up to 100 days', 'High-quality QC photos included with every order', 'Responsive customer support team', 'Wide range of supported product categories'].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <i className="ri-check-line text-secondary-500 mt-0.5 flex-shrink-0"></i>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-5 rounded-lg bg-background-50 border border-accent-200/50">
              <h3 className="font-heading text-lg font-semibold text-accent-600 mb-3 flex items-center gap-2">
                <i className="ri-information-line"></i> Considerations
              </h3>
              <ul className="space-y-2 text-sm text-foreground-700">
                {['Shipping costs vary significantly by destination', 'Some rare items may have longer procurement times', 'International shipping times depend on method chosen', 'Currency exchange rates can affect final pricing', 'Volume weight calculations for bulky items'].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <i className="ri-information-line text-accent-400 mt-0.5 flex-shrink-0"></i>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Shipping Cost Guide */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3 text-center">
            Sugargoo Shipping Cost Guide
          </h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto text-center mb-10">
            Understanding Sugargoo shipping costs helps you budget effectively. Shipping fees depend on package weight, destination country, shipping method, insurance, and parcel volume.
          </p>

          <div className="overflow-x-auto">
            <table className="w-full max-w-3xl mx-auto border-collapse bg-background-100 rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-background-200">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground-800">Shipping Method</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground-800">Speed</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground-800">Cost Level</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground-800">Best For</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200/70">
                {[
                  { method: 'EMS', speed: '7-15 days', cost: 'Medium', best: 'Balanced speed & cost' },
                  { method: 'E-EMS', speed: '10-20 days', cost: 'Low-Medium', best: 'Budget-conscious orders' },
                  { method: 'DHL', speed: '3-7 days', cost: 'High', best: 'Urgent deliveries' },
                  { method: 'SAL', speed: '15-30 days', cost: 'Low', best: 'Non-urgent large parcels' },
                  { method: 'Sea Mail', speed: '30-60 days', cost: 'Lowest', best: 'Heavy & bulky items' },
                ].map((row) => (
                  <tr key={row.method} className="hover:bg-background-50/50">
                    <td className="px-4 py-3 text-sm font-medium text-foreground-800">{row.method}</td>
                    <td className="px-4 py-3 text-sm text-foreground-600">{row.speed}</td>
                    <td className="px-4 py-3 text-sm text-foreground-600">{row.cost}</td>
                    <td className="px-4 py-3 text-sm text-foreground-600">{row.best}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Warehouse & Legitimacy Combined */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Sugargoo Warehouse Explained
              </h2>
              <p className="text-sm text-foreground-600 mb-4 leading-relaxed">
                The Sugargoo warehouse is your central hub for order management. When you purchase items, they are shipped to the Sugargoo warehouse where they undergo quality inspection, are photographed, and stored until you are ready to ship internationally.
              </p>
              <ul className="space-y-2 text-sm text-foreground-700">
                {[
                  'Free storage for up to 100 days',
                  'Professional QC inspection with detailed photos',
                  'Package consolidation to reduce shipping costs',
                  'Secure packaging for international transit',
                  'Real-time inventory tracking in your dashboard',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0"></i>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Is Sugargoo Legit?
              </h2>
              <p className="text-sm text-foreground-600 mb-4 leading-relaxed">
                Sugargoo is a legitimate and well-established shopping agent platform trusted by thousands of international buyers worldwide. The platform has built a strong reputation for reliable service, transparent pricing, and consistent order fulfillment.
              </p>
              <ul className="space-y-2 text-sm text-foreground-700">
                {[
                  'Verify seller ratings before purchasing',
                  'Review QC photos carefully upon warehouse arrival',
                  'Understand shipping policies and insurance options',
                  'Protect payment information with secure checkout',
                  'Read community reviews for product insights',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <i className="ri-shield-check-line text-secondary-500 mt-0.5 flex-shrink-0"></i>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Coupons */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3 text-center">
            Latest Sugargoo Coupon & Discount Codes
          </h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto text-center mb-10">
            Save on your Sugargoo orders with these active coupon codes and discount offers. Check back regularly for new deals.
          </p>

          <div className="overflow-x-auto">
            <table className="w-full max-w-2xl mx-auto border-collapse bg-background-100 rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-accent-100">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground-800">Coupon Code</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground-800">Discount</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground-800">Expiration</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground-800">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200/70">
                {coupons.map((coupon) => (
                  <tr key={coupon.name} className="hover:bg-background-50/50">
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold">
                        {coupon.name}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-primary-700">{coupon.discount}</td>
                    <td className="px-4 py-3 text-sm text-foreground-600">{coupon.expiry}</td>
                    <td className="px-4 py-3 text-sm text-foreground-600">{coupon.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 9 SEO Article Cards Section */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
              Sugargoo Spreadsheet Guides & Articles
            </h2>
            <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
              In-depth guides, honest reviews, and expert tips to help you master Sugargoo shopping. Written for beginners and experienced shoppers alike.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {articles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group flex flex-col rounded-xl bg-background-50 border border-background-200/50 hover:border-primary-200 overflow-hidden transition-all duration-200 no-underline hover:shadow-md"
              >
                <div className="w-full h-44 md:h-48 overflow-hidden">
                  <img
                    src={article.imageUrl}
                    alt={article.imageAlt}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                    title={article.title}
                    loading="lazy"
                  />
                </div>
                <div className="p-4 md:p-5 flex flex-col flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                      Article
                    </span>
                    <span className="text-xs text-foreground-500 flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {article.readTime}
                    </span>
                  </div>
                  <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950 mb-2 group-hover:text-primary-700 transition-colors leading-snug">
                    {article.title}
                  </h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed mb-3 flex-1">
                    {article.excerpt}
                  </p>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600 group-hover:text-primary-700 whitespace-nowrap">
                    Read Article
                    <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform"></i>
                  </span>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/sugargoo-spreadsheet-guide"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-secondary-500 text-background-50 text-base font-semibold hover:bg-secondary-600 transition-colors no-underline cursor-pointer whitespace-nowrap"
            >
              <i className="ri-book-open-line"></i>
              Read The Complete Guide
            </Link>
          </div>
        </div>
      </section>

      {/* Original Article CTA */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Want To Learn More About Sugargoo Spreadsheet?
          </h2>
          <p className="text-foreground-600 text-sm md:text-base mb-8">
            Read our comprehensive guide covering everything from getting started to advanced shopping strategies, product discovery tips, and expert recommendations.
          </p>
          <Link
            to="/sugargoo-spreadsheet-guide"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-secondary-500 text-background-50 text-base font-semibold hover:bg-secondary-600 transition-colors no-underline cursor-pointer whitespace-nowrap"
          >
            <i className="ri-article-line"></i>
            Read The Complete Guide
          </Link>
        </div>
      </section>
    </>
  );
}